import type { ReactNode } from 'react';
import { Card, Descriptions, Tag, Space, Button, Typography, Spin } from 'antd';
import {
  MailOutlined,
  TeamOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '../../stores/sessionStore';
import { useEmailConfig } from '../../api/email-config';
import { useContacts } from '../../api/contacts';

const ROLE_LABEL: Record<string, string> = {
  admin: '管理员',
  editor: '编辑',
  viewer: '审核员',
};

/** 首次进入产品引导：管理员且 SMTP 或联系人未配置时，提示去配置 */
function SetupGuide({ tid }: { tid: string }) {
  const navigate = useNavigate();
  const cfg = useEmailConfig(tid);
  const contacts = useContacts(tid);

  if (cfg.isLoading || contacts.isLoading) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  const smtpDone = !!cfg.data?.has_password;
  const contactsDone = (contacts.data?.length ?? 0) > 0;
  if (smtpDone && contactsDone) return null; // 都配好了，不再提示

  const Item = ({
    done,
    icon,
    title,
    desc,
    action,
    onClick,
  }: {
    done: boolean;
    icon: ReactNode;
    title: string;
    desc: string;
    action: string;
    onClick: () => void;
  }) => (
    <Space
      align="start"
      style={{
        width: '100%',
        justifyContent: 'space-between',
        padding: '12px 0',
      }}
    >
      <Space align="start">
        {done ? (
          <CheckCircleFilled style={{ color: '#52c41a', fontSize: 20, marginTop: 2 }} />
        ) : (
          <ExclamationCircleFilled style={{ color: '#faad14', fontSize: 20, marginTop: 2 }} />
        )}
        <div>
          <Space size="small">
            {icon}
            <Typography.Text strong>{title}</Typography.Text>
            {done ? <Tag color="success">已配置</Tag> : <Tag color="warning">待配置</Tag>}
          </Space>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {desc}
            </Typography.Text>
          </div>
        </div>
      </Space>
      {!done && (
        <Button type="primary" onClick={onClick}>
          {action}
        </Button>
      )}
    </Space>
  );

  return (
    <Card
      title={
        <Space>
          <ExclamationCircleFilled style={{ color: '#faad14' }} />
          <span>完成产品初始配置</span>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
        上传 BOM / ECN
        后需通过邮件通知联系人。请先完成以下两项配置，之后上传时才能正常发送通知邮件。
      </Typography.Paragraph>
      <Item
        done={smtpDone}
        icon={<MailOutlined />}
        title="发件邮箱（SMTP）"
        desc="配置本产品的发件人 SMTP，邮件将以此账号发出。"
        action="去配置"
        onClick={() => navigate('/email-config')}
      />
      <div style={{ borderTop: '1px solid #f0f0f0' }} />
      <Item
        done={contactsDone}
        icon={<TeamOutlined />}
        title="通知联系人"
        desc="添加接收 BOM / ECN 通知的联系人邮箱（可从联系人分组导入）。"
        action="去添加"
        onClick={() => navigate('/members?tab=contacts')}
      />
    </Card>
  );
}

export function DashboardPage() {
  const { user, currentTenant, role } = useSessionStore();
  const isAdmin = !!user?.is_platform_admin || role === 'admin';

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {isAdmin && currentTenant && <SetupGuide tid={currentTenant.id} />}
      <Card title="当前会话">
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="用户">
            {user?.name}（{user?.email}）
          </Descriptions.Item>
          <Descriptions.Item label="平台管理员">
            {user?.is_platform_admin ? <Tag color="gold">是</Tag> : <Tag>否</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="当前产品">
            {currentTenant ? (
              <Space>
                <Tag color="blue">{currentTenant.name}</Tag>
                <Tag>{currentTenant.code}</Tag>
              </Space>
            ) : (
              '（未选择 / 平台模式）'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="角色">
            {role ? (ROLE_LABEL[role] ?? role) : '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </Space>
  );
}
